import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { programmesListingPage } from "src/DAL/Programmes/Programmes";
import { htmlText } from "./programmeHelpers";

// How many programmes come per page; Load more fetches the next page.
export const PROGRAMMES_PAGE = 50;
const SEARCH_DELAY_MS = 400;

const textOf = (item) =>
  `${htmlText(item?.title || item?._id?.title)} ${item?._id?.short_description || ""}`.toLowerCase();

/**
 * The member's programme list (Programmes, Videos Library) from the API, 50 at a time.
 * Searching asks the API (the database searches title / short description) once typing pauses;
 * Load more fetches the next page of the same search.
 */
export default function useProgrammeSearch(queryKey, onError) {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const list = useInfiniteQuery({
    queryKey: [queryKey, search],
    queryFn: async ({ pageParam = 0 }) => {
      const result = await programmesListingPage({ page: pageParam, limit: PROGRAMMES_PAGE, search });
      if (result?.code !== 200) throw new Error(result?.message);
      return result;
    },
    getNextPageParam: (last, pages) => (last?.has_more ? pages.length : undefined),
    // Keep the current cards on screen while a new search loads.
    keepPreviousData: true,
    onError: (error) => onError && onError(error),
  });

  const visible = useMemo(() => {
    const pages = list.data?.pages || [];
    const items = pages.flatMap((page) =>
      (page?.program?.program || []).filter((item) => item?._id !== null)
    );
    // An API without paging (no `total`) sends every programme and ignores the search:
    // filter here so search still works until that backend is updated.
    if (pages[0] && pages[0].total === undefined && search) {
      const needle = search.toLowerCase();
      return items.filter((item) => textOf(item).includes(needle));
    }
    return items;
  }, [list.data, search]);

  return {
    query,
    setQuery,
    searching: Boolean(search),
    visible,
    total: list.data?.pages?.[0]?.total ?? visible.length,
    isLoading: list.isLoading,
    isSearching: list.isFetching && !list.isFetchingNextPage,
    hasMore: Boolean(list.hasNextPage),
    loadingMore: list.isFetchingNextPage,
    loadMore: () => list.fetchNextPage(),
    refetch: list.refetch,
  };
}
